import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface FormCycle {
  id: string
  startsAt: string
  endsAt?: string
}

interface MaterializedForm {
  id: string
  startsAt?: string | null
  endsAt?: string | null
}

interface ResourceWithFormCycles {
  form?: {
    materializedForms?: MaterializedForm[]
    schedule?: {
      cycles?: FormCycle[]
    } | null
  }
}

// Structured localStorage for filters and cycle selection
export interface FormFiltersStorage {
  filters: Record<string, unknown>
  cycleId?: string
}

/**
 * Manages form cycle selection with URL/localStorage sync.
 * Prefers materializedForms over schedule.cycles.
 * Supports multiple GraphQL fragments, sorts by start date descending.
 */
export const useFormCycleSelection = (
  resource: ResourceWithFormCycles | null | undefined,
  savedStorage?: FormFiltersStorage
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const sortedCycles = useMemo(() => {
    // Prefer materializedForms over schedule.cycles
    const materializedForms = resource?.form?.materializedForms
    const legacyCycles = resource?.form?.schedule?.cycles

    if (materializedForms && materializedForms.length > 0) {
      const normalizedCycles: FormCycle[] = materializedForms
        .filter((mf): mf is MaterializedForm & { startsAt: string } => !!mf.startsAt)
        .map((mf) => ({
          id: mf.id,
          startsAt: mf.startsAt,
          endsAt: mf.endsAt || undefined,
        }))

      return normalizedCycles.sort(
        (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()
      )
    }

    // Fallback to legacy cycles
    if (!legacyCycles) return []

    const normalizedCycles: FormCycle[] = legacyCycles.map((cycle) => ({
      id: cycle.id,
      startsAt: cycle.startsAt,
      endsAt: cycle.endsAt || undefined,
    }))

    return normalizedCycles.sort(
      (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()
    )
  }, [resource])

  const urlCycleId = searchParams.get('cycleId')
  const savedCycleId = savedStorage?.cycleId
  const defaultCycleId = sortedCycles?.[0]?.id ?? null

  const validUrlCycleId = urlCycleId && sortedCycles.find((cycle) => cycle.id === urlCycleId)?.id
  const validSavedCycleId =
    savedCycleId && sortedCycles.find((cycle) => cycle.id === savedCycleId)?.id

  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(
    validUrlCycleId ?? validSavedCycleId ?? defaultCycleId ?? null
  )

  const handleCycleChange = useCallback(
    (cycleId: string | null) => {
      setSelectedCycleId(cycleId)

      if (cycleId) {
        setSearchParams({ cycleId })
      } else {
        setSearchParams({})
      }
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (sortedCycles.length > 0) {
      if (validUrlCycleId && selectedCycleId !== validUrlCycleId) {
        setSelectedCycleId(validUrlCycleId)
      } else if (validSavedCycleId && !validUrlCycleId && selectedCycleId !== validSavedCycleId) {
        setSelectedCycleId(validSavedCycleId)
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.set('cycleId', validSavedCycleId)
          return next
        })
      } else if (!validUrlCycleId && !validSavedCycleId && !selectedCycleId && defaultCycleId) {
        setSelectedCycleId(defaultCycleId)
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.set('cycleId', defaultCycleId)
          return next
        })
      }
    }
  }, [
    sortedCycles.length,
    validUrlCycleId,
    validSavedCycleId,
    selectedCycleId,
    defaultCycleId,
    setSearchParams,
  ])

  return {
    cycles: sortedCycles,
    selectedCycleId,
    handleCycleChange,
    hasCycles: sortedCycles.length > 1,
  }
}
